import { createContext, useContext } from "react";
import type { TranslatedString } from "@i18n";
import type { ChatConversation, ChatSection, ChatMessage, ChatMember } from "../api/portalChat.api";

export type ThreadData = {
  messages: ChatMessage[];
  members: ChatMember[];
  memberReadDates: { userId: string; lastReadDate: number }[];
  hasMore: boolean;
  loading: boolean;
  /** Load failed — the thread pane renders "Couldn't load this conversation" with Retry (PRD §4.16). */
  error?: boolean;
};

export type ComposerIntent = { kind: "reply"; message: ChatMessage } | null;

/** A send that failed (server refusal or network) — kept so Retry posts into ITS conversation. */
export type FailedSend = {
  conversationId: string;
  text: string;
  attachments: { key: string; name: string; type?: string; size?: number }[];
  replyToMessageId?: string;
  mentions: string[];
};

export type ChatScope = "all" | "direct" | "group" | "project";

export type PortalChatContextValue = {
  userId: string;
  userName: string;
  conversations: ChatConversation[];
  sections: ChatSection[];
  projectOrder: string[];
  loadingList: boolean;
  /** True once this portal session's conversation list has loaded at least once. */
  listLoaded: boolean;
  connected: boolean;
  selectedId: string | null;
  thread: ThreadData | null;
  typingUsers: { userId: string; userName: string }[];
  composerIntent: ComposerIntent;
  highlightMessageId: string | null;
  currentProjectId: string | null;
  /** PRD §4.7: the scope lives in the panel header and persists per tab. */
  scope: ChatScope;
  setScope: (scope: ChatScope) => void;
  /** PRD §4.8: the unread filter; the thread pane renders its own empty state under it. */
  unreadOnly: boolean;
  toggleUnreadOnly: () => void;

  selectConversation: (conversationId: string | null) => void;
  refreshList: () => Promise<void>;
  loadOlderMessages: () => Promise<void>;
  sendMessage: (args: {
    text: string;
    attachments?: { key: string; name: string; type?: string; size?: number }[];
    replyToMessageId?: string;
    forwardOfMessageId?: string;
    mentions?: string[];
    /** Explicit target — a failed-send Retry must post into ITS conversation, not the open one. */
    conversationId?: string;
  }) => Promise<boolean>;
  editMessage: (messageId: string, text: string) => Promise<boolean>;
  deleteMessage: (messageId: string) => Promise<void>;
  deleteAttachment: (messageId: string, awsKey: string) => Promise<void>;
  toggleReaction: (messageId: string, emoji: string) => Promise<void>;
  setComposerIntent: (intent: ComposerIntent) => void;
  setHighlightMessageId: (messageId: string | null) => void;
  updateConversationState: (
    conversationId: string,
    patch: { hidden?: boolean; muted?: boolean; sectionId?: string | null },
  ) => Promise<void>;
  emitTyping: (conversationId: string, isTyping: boolean) => void;
  /** Advance the read cursor without opening the conversation (row menu "Mark as read"). */
  markConversationRead: (conversationId: string) => void;
  openForwardDialog: (message: ChatMessage) => void;
  openReceiptsDialog: (message: ChatMessage) => void;
  openReactionsDialog: (message: ChatMessage) => void;
  openEditDialog: (message: ChatMessage) => void;
  /** Un-hides a conversation and opens it (search resurfacing, forward destination — PRD §4.11). */
  unhideAndOpen: (conversationId: string) => void;
  /** Select the next available conversation after `conversationId` leaves the list (hide/removal). */
  selectNextAfter: (conversationId: string) => void;
  /** Bumps whenever the panel is maximised/restored — open menus close themselves on change (PRD §4.6). */
  menuEpoch: number;
  jumpToMessage: (conversationId: string, messageId: string) => void;
  /** Failed-send bubble state lives in the core so it survives screen unmounts on mobile. */
  failedSend: FailedSend | null;
  setFailedSend: (failed: FailedSend | null) => void;
  showToast: (content: TranslatedString, success?: boolean) => void;
  /** Toast a server errorMessage (fixed English copy) through i18n, falling back when unknown. */
  showServerError: (serverMessage: string | undefined, fallback: TranslatedString) => void;
};

export const PortalChatContext = createContext<PortalChatContextValue | null>(null);

export function usePortalChat(): PortalChatContextValue {
  const value = useContext(PortalChatContext);
  if (!value) throw new Error("usePortalChat must be used inside PortalChatContext");
  return value;
}

export function isSameDay(a: number, b: number): boolean {
  const da = new Date(a);
  const db = new Date(b);
  return da.getFullYear() === db.getFullYear() && da.getMonth() === db.getMonth() && da.getDate() === db.getDate();
}

export function conversationDisplayTime(timestamp: number): string {
  const date = new Date(timestamp);
  if (isSameDay(timestamp, Date.now())) {
    return date.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
  }
  return date.toLocaleDateString(undefined, { day: "numeric", month: "short" });
}

export function messageTime(timestamp: number): string {
  return new Date(timestamp).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
}

export function dayLabel(timestamp: number, todayText: string, yesterdayText: string): string {
  const nowMs = Date.now();
  if (isSameDay(timestamp, nowMs)) return todayText;
  if (isSameDay(timestamp, nowMs - 24 * 60 * 60 * 1000)) return yesterdayText;
  return new Date(timestamp).toLocaleDateString(undefined, { day: "numeric", month: "long", year: "numeric" });
}
